/**
 * Where a customer was heading before being sent to sign in (DF-230), e.g. a
 * one-off product link from a campaign email.
 *
 * The login URL carries it as `?redirect=`, which the password flow honours.
 * But the magic-link email flow leaves the app and returns on a fresh page
 * carrying only a token, so the `?redirect=` is gone by then. To survive that
 * round-trip the intent is also stashed here in localStorage (shared across
 * tabs in the same browser, so the email opening in a new tab still finds it).
 * A different browser won't have it and simply falls back to My Deliveries.
 */
const KEY = 'post_login_redirect'

/** Only ever an internal path, never an absolute or protocol-relative URL. */
const isInternalPath = (path: string | null | undefined): path is string =>
  !!path && path.startsWith('/') && !path.startsWith('//')

export function rememberPostLoginRedirect(path: string | null | undefined): void {
  if (!isInternalPath(path)) return
  try {
    localStorage.setItem(KEY, path)
  } catch {
    // Storage unavailable (private mode); the ?redirect= param still covers the
    // password flow, and the magic-link flow simply falls back to the dashboard.
  }
}

/** The stored path (validated) if there is one, else null. Clears it either way. */
export function consumePostLoginRedirect(): string | null {
  let stored: string | null = null
  try {
    stored = localStorage.getItem(KEY)
    localStorage.removeItem(KEY)
  } catch {
    // ignore
  }
  return isInternalPath(stored) ? stored : null
}
